import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Eye, Trash2, FileText, AlertTriangle, ExternalLink, Clock, Calendar, Edit, Copy, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get due date status
const getDueDateStatus = (order) => {
  if (!order.scheduled_datetime || order.status === 'Completed') return null;
  
  const now = new Date();
  const dueDate = new Date(order.scheduled_datetime);
  const diffMs = dueDate - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMs < 0) {
    return { 
      label: "OVERDUE", 
      color: "bg-red-500/20 text-red-400 border-red-500/50",
      icon: AlertTriangle,
      priority: 0
    };
  } else if (diffDays === 0) {
    return { 
      label: "DUE TODAY", 
      color: "bg-orange-500/20 text-orange-400 border-orange-500/50",
      icon: Clock,
      priority: 1
    };
  } else if (diffDays <= 3) {
    return { 
      label: "DUE SOON", 
      color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
      icon: Calendar,
      priority: 2
    };
  }
  return null;
};

const SPDOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [statusChangeOrder, setStatusChangeOrder] = useState(null);
  const [statusComment, setStatusComment] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [newComment, setNewComment] = useState("");
  const [commentOrderId, setCommentOrderId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/spd/orders`;
      if (statusFilter !== "all") {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteOrderId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/spd/orders/${deleteOrderId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Order deleted successfully");
        fetchOrders();
      } else {
        toast.error("Failed to delete order");
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    } finally {
      setShowDeleteDialog(false);
      setDeleteOrderId(null);
    }
  };

  const handleStatusChange = (order, status) => {
    if (status === "Rejected" || status === "Revoked") {
      setStatusChangeOrder(order);
      setNewStatus(status);
      setStatusComment("");
      setShowStatusDialog(true);
    } else {
      updateStatus(order._id, status, null);
    }
  };

  const updateStatus = async (orderId, status, comment) => {
    try {
      const response = await fetch(`${API_URL}/api/spd/orders/${orderId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, comment })
      });
      
      if (response.ok) {
        toast.success("Status updated successfully");
        fetchOrders();
        setShowStatusDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleClone = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/spd/orders/${orderId}/clone`, {
        method: "POST"
      });
      
      if (response.ok) {
        toast.success("Order cloned successfully");
        fetchOrders();
      } else {
        toast.error("Failed to clone order");
      }
    } catch (error) {
      console.error("Error cloning order:", error);
      toast.error("Failed to clone order");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !commentOrderId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/spd/orders/${commentOrderId}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment: newComment })
      });
      
      if (response.ok) {
        toast.success("Comment added successfully");
        setShowCommentDialog(false);
        setNewComment("");
        fetchOrders();
      } else {
        toast.error("Failed to add comment");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };

  const filteredOrders = orders.filter(order => 
    order.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.product_id && order.product_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.company_name && order.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.purchase_order_number && order.purchase_order_number.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'In Progress': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'Completed': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'Rejected': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'Revoked': return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
      case 'Delivered': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50';
      default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-blue-400 uppercase tracking-wider">
            Received Purchase Order
          </h1>
          <p className="text-gray-400 mt-1">Manage and track all SPD orders</p>
        </div>
        <Button
          onClick={() => navigate("/spd/create-order")}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold uppercase tracking-wider shadow-lg"
          data-testid="create-order-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Order
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-black/80 border-2 border-blue-500/40">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                data-testid="spd-search-input"
                placeholder="Search by item, company, PO number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-900/80 border-gray-700 text-white placeholder:text-gray-500"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 bg-gray-900/80 border-gray-700 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="all" className="text-white">All Status</SelectItem>
                <SelectItem value="Available" className="text-blue-400">Available</SelectItem>
                <SelectItem value="In Progress" className="text-orange-400">In Progress</SelectItem>
                <SelectItem value="Completed" className="text-emerald-400">Completed</SelectItem>
                <SelectItem value="Delivered" className="text-cyan-400">Delivered</SelectItem>
                <SelectItem value="Rejected" className="text-red-400">Rejected</SelectItem>
                <SelectItem value="Revoked" className="text-gray-400">Revoked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-black/80 border-2 border-blue-500/40 shadow-lg overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-gray-400">Loading orders...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No orders found</p>
              <p className="text-gray-500 text-sm mt-1">
                {searchTerm ? "Try adjusting your search" : "Create your first order to get started"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/30 bg-blue-500/10">
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">Item Name</th>
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">Company</th>
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">PO Number</th>
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">Total Qty</th>
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">Due Date</th>
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">Status</th>
                    <th className="text-left p-4 text-blue-400 font-bold uppercase text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const dueStatus = getDueDateStatus(order);
                    return (
                    <tr 
                      key={order._id} 
                      className={`border-b border-blue-500/20 hover:bg-blue-500/10 transition-colors ${dueStatus?.priority === 0 ? 'bg-red-500/5' : ''}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{order.item_name}</span>
                          {order.note && (
                            <span className="text-yellow-400 text-xs" title="Has note">📝</span>
                          )}
                          {order.image_drive_url && (
                            <span className="text-green-400 text-xs" title="Has image">📷</span>
                          )}
                          {order.comments?.length > 0 && (
                            <span className="text-blue-400 text-xs" title={`${order.comments.length} comments`}>💬</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{order.product_id || ""}</span>
                      </td>
                      <td className="p-4 text-gray-300">{order.company_name || "-"}</td>
                      <td className="p-4 text-gray-300">{order.purchase_order_number || "-"}</td>
                      <td className="p-4 text-gray-300">{order.total_quantity}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-gray-300 text-sm">
                            {order.scheduled_datetime 
                              ? new Date(order.scheduled_datetime).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
                              : "-"}
                          </span>
                          {dueStatus && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${dueStatus.color}`}>
                              <dueStatus.icon className="h-3 w-3" />
                              {dueStatus.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Select
                          value={order.status}
                          onValueChange={(value) => handleStatusChange(order, value)}
                        >
                          <SelectTrigger className={`w-32 h-8 text-xs font-bold border ${getStatusColor(order.status)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-gray-700">
                            <SelectItem value="Available" className="text-blue-400">Available</SelectItem>
                            <SelectItem value="In Progress" className="text-orange-400">In Progress</SelectItem>
                            <SelectItem value="Completed" className="text-emerald-400">Completed</SelectItem>
                            <SelectItem value="Delivered" className="text-cyan-400">Delivered</SelectItem>
                            <SelectItem value="Rejected" className="text-red-400">Rejected</SelectItem>
                            <SelectItem value="Revoked" className="text-gray-400">Revoked</SelectItem>
                          </SelectContent>
                        </Select>
                        {order.status_comment && (
                          <p className="text-xs text-gray-500 mt-1 truncate max-w-[120px]" title={order.status_comment}>
                            💬 {order.status_comment}
                          </p>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowViewDialog(true);
                            }}
                            className="h-8 w-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/spd/edit-order/${order._id}`)}
                            className="h-8 w-8 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleClone(order._id)}
                            className="h-8 w-8 text-green-400 hover:text-green-300 hover:bg-green-500/20"
                            title="Clone"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setCommentOrderId(order._id);
                              setShowCommentDialog(true);
                            }}
                            className="h-8 w-8 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20"
                            title="Add Comment"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeleteOrderId(order._id);
                              setShowDeleteDialog(true);
                            }}
                            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Order Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-gray-900 border-blue-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-400">Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Item Name</p>
                  <p className="text-white font-medium">{selectedOrder.item_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Product ID</p>
                  <p className="text-white font-medium">{selectedOrder.product_id || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Company Name</p>
                  <p className="text-white font-medium">{selectedOrder.company_name || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Purchase Order Number</p>
                  <p className="text-white font-medium">{selectedOrder.purchase_order_number || "-"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Quantity</p>
                  <p className="text-white font-medium">{selectedOrder.total_quantity}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Delivery Due Date</p>
                  <p className="text-white font-medium">{formatDate(selectedOrder.scheduled_datetime)}</p>
                </div>
              </div>

              {selectedOrder.status_comment && (
                <div>
                  <p className="text-gray-400 text-sm">Status Comment</p>
                  <div className="bg-gray-800 rounded-lg p-3 mt-1">
                    <p className="text-gray-300">{selectedOrder.status_comment}</p>
                  </div>
                </div>
              )}

              {selectedOrder.note && (
                <div>
                  <p className="text-gray-400 text-sm">Note</p>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-1">
                    <p className="text-yellow-200">{selectedOrder.note}</p>
                  </div>
                </div>
              )}

              {selectedOrder.comments && selectedOrder.comments.length > 0 && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Comments ({selectedOrder.comments.length})</p>
                  <div className="space-y-2">
                    {selectedOrder.comments.map((comment, idx) => (
                      <div key={idx} className="bg-gray-800 rounded-lg p-3">
                        <p className="text-gray-300">{comment.text}</p>
                        <p className="text-gray-500 text-xs mt-1">{formatDate(comment.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedOrder.image_drive_url && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Image (Scan QR Code)</p>
                  <div className="bg-gray-800 rounded-lg p-4 flex items-start gap-4">
                    <div className="bg-white p-2 rounded-lg flex-shrink-0">
                      <QRCodeSVG 
                        value={selectedOrder.image_drive_url} 
                        size={100}
                        level="M"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-400 mb-2">Scan QR code to view image</p>
                      <a 
                        href={selectedOrder.image_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open in Google Drive
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-gray-500 text-xs">
                Created: {formatDate(selectedOrder.created_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog (for Rejected/Revoked) */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="bg-gray-900 border-yellow-500/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-yellow-400">
              Change Status to {newStatus}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-300">
              Please provide a reason for marking this order as <span className="font-bold">{newStatus}</span>.
            </p>
            <div>
              <Label className="text-gray-400">Comment (Required)</Label>
              <Textarea
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                placeholder="Enter reason..."
                className="bg-gray-800 border-gray-700 text-white mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowStatusDialog(false)}
              className="border-gray-600 text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updateStatus(statusChangeOrder._id, newStatus, statusComment)}
              disabled={!statusComment.trim()}
              className={newStatus === "Rejected" ? "bg-red-600 hover:bg-red-700" : "bg-gray-600 hover:bg-gray-700"}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={showCommentDialog} onOpenChange={setShowCommentDialog}>
        <DialogContent className="bg-gray-900 border-purple-500/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-purple-400">
              Add Comment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Comment</Label>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Enter your comment..."
                className="bg-gray-800 border-gray-700 text-white mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCommentDialog(false);
                setNewComment("");
              }}
              className="border-gray-600 text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Add Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Delete Order
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-300">
            Are you sure you want to delete this order? This action cannot be undone.
          </p>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-gray-600 text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SPDOrders;
